import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { Radios } from "../../../../../../MainComponents/Radios/Radios";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { returnIsValid, useConstants } from "../../../Main/useConstants";
import useUnsavedChangesWarning from "../../../../../../../hooks/useUnsavedChangesWarning";
import { useSnackbar } from "notistack";
import { updateInterview } from "../../../../../../../apis";
import { Conclusion } from "./Conclusion";
import { EthiquinEmptyState } from "../../../components/EthiquinEmptyState";
import { sanitizeForInnerHTML } from "../../../../../../../helpers/sanitizeHtml";

export const Analysis = forwardRef(
  (
    {
      evaluation = null,
      id,
      conclusion = null,
      setInterviews,
      isLoading,
      setIsLoading,
      alias,
      interviewed,
      readOnly = false,
    },
    ref
  ) => {

    // Custom hooks
    const theme = useTheme();
    const [t] = useTranslation("investigation");
    const { enqueueSnackbar } = useSnackbar();
    const {
      interviews: { pointsToEvaluateLabels, options },
    } = useConstants();
    // Refs
    const detailsRefs = useRef({});
    const aditionalDetailsRef = useRef(null);
    // snapshot to compare unsaved changes while editing
    const initialPayloadSnapshotRef = useRef(null);
    // Initial States
    const [errors, setErrors] = useState({});
    const [triedToSave, setTriedToSave] = useState(false);
    const [details, setDetails] = useState("");
    const [activeConclusionDetails, setActiveConclusionDetails] =
      useState(false);
    const [visibleDetails, setVisibleDetails] = useState({});
    const [openConclusion, setOpenConclusion] = useState(false);

    // Component-managed editing state:
    // if interview.evaluation === null -> start in edit mode (create mode).
    // En readOnly nunca entramos en modo edición.
    const [internalEditing, setInternalEditing] = useState(
      () => evaluation === null && !readOnly
    );

    // initPointsToEvaluate
    const initPointsToEvaluate = useCallback(() => {
      const points = {};
      Object.entries(pointsToEvaluateLabels).forEach(([key, label]) => {
        points[key] = {
          value: "",
          details: "",
          label,
        };
      });
      return points;
    }, [pointsToEvaluateLabels]);

    const [pointsToEvaluate, setPointsToEvaluate] =
      useState(initPointsToEvaluate);

    /* buildEvaluationPayload */
    const buildEvaluationPayload = useCallback(() => {
      const points = {};
      Object.entries(pointsToEvaluate).forEach(([key, point]) => {
        points[key] = {
          value: point.value || "",
          details:
            point.details === false || point.details === undefined
              ? ""
              : String(point.details),
        };
      });

      return {
        points,
        details: details || "",
      };
    }, [pointsToEvaluate, details]);

    /* validatePointsRequired */
    const validatePointsRequired = useCallback(() => {
      const newErrors = {};
      Object.entries(pointsToEvaluate).forEach(([key, point]) => {
        newErrors[key] = !point.value || point.value === "";
      });

      setErrors(newErrors);
      return !Object.values(newErrors).some(Boolean);
    }, [pointsToEvaluate]);

    /* resetValues */
    const resetValues = useCallback(() => {
      setPointsToEvaluate(initPointsToEvaluate());
      setDetails("");
      setActiveConclusionDetails(false);
      setErrors({});
      setTriedToSave(false);
      setVisibleDetails({});
      detailsRefs.current = {};
      try {
        if (aditionalDetailsRef.current?.blur)
          aditionalDetailsRef.current.blur();
      } catch (e) {
        // noop
      }
    }, [initPointsToEvaluate]);

    /* populateFromEvaluation => prefill fields from interview.evaluation */
    const populateFromEvaluation = useCallback(() => {
      if (!evaluation) return;

      const evalPoints = {};
      Object.entries(pointsToEvaluateLabels).forEach(([key, label]) => {
        const savedPoint = evaluation.points?.[key];
        evalPoints[key] = {
          value: savedPoint?.value || "",
          details: savedPoint?.details || "",
          label,
        };
      });

      setPointsToEvaluate(evalPoints);
      setDetails(evaluation.details || "");
      const visible = {};
      Object.entries(evalPoints).forEach(([k, p]) => {
        if (p.details && p.details.trim() !== "") visible[k] = true;
      });
      setVisibleDetails(visible);
      setActiveConclusionDetails(Boolean(evaluation.details?.trim()));

      // snapshot initial
      const snapshot = JSON.stringify({
        points: Object.fromEntries(
          Object.entries(evalPoints).map(([k, v]) => [
            k,
            { value: v.value, details: v.details },
          ])
        ),
        details: evaluation.details || "",
      });
      initialPayloadSnapshotRef.current = snapshot;
    }, [evaluation, pointsToEvaluateLabels]);

    /* compare snapshot if it exists */
    const hasUnsavedChanges = useCallback(() => {
      if (!internalEditing) return false;

      const currentPayload = JSON.stringify(buildEvaluationPayload());
      if (initialPayloadSnapshotRef.current) {
        return initialPayloadSnapshotRef.current !== currentPayload;
      }

      if (details.trim() !== "") return true;
      return Object.values(pointsToEvaluate).some(
        (point) => point.value !== "" || point.details.trim() !== ""
      );
    }, [internalEditing, buildEvaluationPayload, details, pointsToEvaluate]);

    // Hook for warning of unsaved changes (beforeunload)
    useUnsavedChangesWarning(hasUnsavedChanges());

    /* Handlers */
    const handleChange = useCallback((key, value) => {
      setPointsToEvaluate((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          value,
        },
      }));
      setErrors((prev) => ({ ...prev, [key]: false }));
    }, []);

    const handleChangeDetails = useCallback((key, value) => {
      setPointsToEvaluate((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          details: value,
        },
      }));
    }, []);

    // (shows/hides) the detail field associated with a key in the visibleDetails state
    const toggleShowDetails = useCallback((key) => {
      setVisibleDetails((prev) => {
        const willShow = !prev?.[key];
        const newState = { ...prev, [key]: willShow };

        if (!willShow) return newState;

        setTimeout(() => {
          detailsRefs.current[key]?.focus();
        }, 150);

        return newState;
      });
    }, []);

    const isDetailsVisible = useCallback(
      (key) => Boolean(visibleDetails?.[key]),
      [visibleDetails]
    );

    // Cancel editing
    const handleCancelEdit = useCallback(() => {
      if (evaluation) {
        populateFromEvaluation();
      } else {
        resetValues();
      }
      setInternalEditing(false);
    }, [evaluation, populateFromEvaluation, resetValues]);

    /* Send and close edition for success */
    const handleSubmit = useCallback(async () => {
      setTriedToSave(true);

      const isValid = validatePointsRequired();
      if (!isValid) {
        enqueueSnackbar({
          variant: "error",
          message: t("evaluation.please_complete_all_fields_before_saving"),
        });
        return;
      }

      const evaluation = buildEvaluationPayload();
      const data = {
        alias,
        interviews_id: id,
        interviews_data: {
          evaluation,
        },
      };

      setIsLoading(true);
      try {
        const resp = await updateInterview(data);
        setInterviews(resp.data.interviews);
        // Close edit mode
        setInternalEditing(false);
        // clear snapshot, it will be repopulated the next time it is edited
        initialPayloadSnapshotRef.current = null;
        resetValues();

        enqueueSnackbar({
          variant: "success",
          message: t("Interview.data_saved_successfully"),
        });
      } catch (error) {
        console.error("updateInterview error:", error);
      } finally {
        setIsLoading(false);
      }
    }, [
      validatePointsRequired,
      buildEvaluationPayload,
      alias,
      evaluation,
      setIsLoading,
      updateInterview,
      enqueueSnackbar,
      setInterviews,
      resetValues,
      t,
    ]);

    /* evaluationPoints (view mode) */
    const evaluationPoints = useMemo(() => {
      if (!evaluation) return [];
      let points = Object.entries(evaluation.points).map(([key, value]) => ({
        key,
        value,
      }));
      points = points.filter((point) => point.value?.value);
      return points;
    }, [evaluation]);

    // If the evaluation changes from the server, clear local state and exit editing
    useEffect(() => {
      if (evaluation) {
        setInternalEditing(false);
        resetValues();
        setVisibleDetails({});
        setActiveConclusionDetails(false);

        try {
          Object.values(detailsRefs.current).forEach((el) => {
            if (el?.blur) el.blur();
          });
          if (aditionalDetailsRef.current?.blur)
            aditionalDetailsRef.current.blur();
        } catch (e) {
          // noop
        }
      } else if (!readOnly) {
        // if the evaluation was removed, enter edit mode (new)
        setInternalEditing(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [evaluation, readOnly]);

    // If the user requests to edit, populate and open editing
    const handleStartEdit = useCallback(() => {
      if (evaluation) {
        populateFromEvaluation();
      } else {
        // init values
        resetValues();
      }
      setInternalEditing(true);
    }, [
      evaluation,
      populateFromEvaluation,
      resetValues,
      // pointsToEvaluate,
    ]);

    // Expose API to parent
    const restoreSnapshot = useCallback(() => {
      if (evaluation) {
        populateFromEvaluation();
      } else {
        resetValues();
      }
      setInternalEditing(false);
    }, [evaluation, populateFromEvaluation, resetValues]);

    useImperativeHandle(
      ref,
      () => ({
        hasUnsavedChanges,
        restoreSnapshot,
        hdlOpenConclusion: () => setOpenConclusion(true),
      }),
      [hasUnsavedChanges, restoreSnapshot]
    );
    

    if (readOnly && !evaluation) {
      return (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 4, md: 5 },
            borderRadius: 2,
            mx: "auto",
            width: "100%",
            maxWidth: 800,
          }}
        >
          <Typography variant="h5" color="primary" lineHeight={1} mb={1}>
            {t("Interview.interview_evaluation")}
          </Typography>
          <EthiquinEmptyState
            title={t("Interview.no_evaluation_recorded")}
            height="66vh"
            imageWidth={{ xs: "70%", md: 350 }}
          />
          {!!conclusion && (
            <Box mt={2}>
              <Typography
                variant="h5"
                color="primary"
                lineHeight={1}
                sx={{
                  borderTop: `2px solid ${theme.palette.blueGray200.main}`,
                  pt: 1.5,
                }}
              >
                {t("Interview.conclusion")}
              </Typography>
              <Typography
                component="div"
                py={1}
                dangerouslySetInnerHTML={{
                  __html: sanitizeForInnerHTML(conclusion),
                }}
              />
            </Box>
          )}
        </Paper>
      );
    }

    return (
      <>
        <Conclusion
          openConclusion={openConclusion}
          setOpenConclusion={setOpenConclusion}
          summary={conclusion}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          alias={alias}
          interviewed={interviewed}
          idInterview={id}
          setInterviews={setInterviews}
          readOnly={readOnly}
        />
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 4, md: 5 },
            borderRadius: 2,
            mx: "auto",
            width: "100%",
            maxWidth: 800,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              borderBottom: `2px solid ${theme.palette.blueGray200.main}`,
              mb: 0.75,
            }}
          >
            {/* Title */}
            <Box>
              <Typography variant="h5" color="primary" lineHeight={1}>
                {t("Interview.interview_evaluation")}
              </Typography>
              <Typography
                variant="body1"
                fontSize={14}
                py={0.75}
                px={0.5}
                sx={{ textIndent: "1em" }}
              >
                {!evaluation || internalEditing
                  ? t("Interview.analyze_and_assess_interview_results")
                  : ""}
              </Typography>
            </Box>
            {/* Edit button */}
            <Box mt={-0.75}>
              {!readOnly && evaluation && !internalEditing && (
                <EGIconButton
                  aria-label="edit-evaluation"
                  title={t("evaluation.edit_evaluation")}
                  onClick={handleStartEdit}
                  iconName="outline_edit"
                  iconSize={20}
                  placement="left-end"
                />
              )}
            </Box>
          </Box>

          {internalEditing ? (
            <>
              {/* Evaluation Form */}
              <Box>
                {Object.entries(pointsToEvaluate).map(([key, point]) => (
                  <Box
                    key={key}
                    sx={{
                      py: 0.7,
                      borderBottom: `1px solid ${theme.palette.blueGray50.main}`,
                    }}
                  >
                    <Radios
                      value={point.value}
                      onChangue={(e) => handleChange(key, e)}
                      text={point.label}
                      options={options}
                      disabled={isLoading}
                      row
                      sxLabel={{
                        fontSize: 13,
                        mb: 0.2,
                        mx: 0.5,
                        fontWeight: 600,
                        ...(returnIsValid(errors[key], triedToSave) ===
                          false && {
                          color: theme.palette.red.main,
                        }),
                      }}
                    />
                    {point.value && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-end",
                          flexFlow: "column nowrap",
                        }}
                      >
                        {point.details.trim() === "" && (
                          <Link
                            onClick={() => toggleShowDetails(key)}
                            underline="hover"
                            sx={{ display: "inline-block", mb: 0.5, mr: 0.75 }}
                          >
                            {isDetailsVisible(key)
                              ? `${t("evaluation.hide")} ▾`
                              : `${t("evaluation.add_specific_comment")} ▸`}
                          </Link>
                        )}

                        {isDetailsVisible(key) && (
                          <Box
                            sx={{
                              mt: point.details.trim() === "" ? 0 : 1,
                              mb: 0.5,
                              px: 0.5,
                              width: "100%",
                            }}
                          >
                            <TextField
                              label={`${t("evaluation.specific_comment")}:`}
                              placeholder={t(
                                "evaluation.briefly_describe_your_comment_note_about_this_point"
                              )}
                              fullWidth
                              multiline
                              minRows={1}
                              maxRows={5}
                              value={point.details ?? ""}
                              onChange={(e) =>
                                handleChangeDetails(key, e.target.value)
                              }
                              sx={{
                                "& .MuiInputBase-input": {
                                  fontSize: "0.9rem",
                                  my: -1,
                                },
                              }}
                              inputRef={(el) => (detailsRefs.current[key] = el)}
                              disabled={isLoading}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}

                <Box
                  sx={{
                    borderTop: `1px solid ${theme.palette.blueGray100.main}`,
                  }}
                >
                  <Box
                    mt={1}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      flexFlow: "column nowrap",
                    }}
                  >
                    {details.trim() === "" && (
                      <Link
                        onClick={() =>
                          setActiveConclusionDetails((show) => {
                            const newState = !show;
                            if (!show) {
                              setTimeout(() => {
                                // Make sure the input exists.
                                if (aditionalDetailsRef.current) {
                                  aditionalDetailsRef.current.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                  });
                                  aditionalDetailsRef.current.focus({
                                    preventScroll: true,
                                  });
                                }
                              }, 150);
                            }
                            return newState;
                          })
                        }
                        underline="hover"
                        textAlign="end"
                        sx={{ display: "inline-block", mb: 1, ml: 0.75 }}
                      >
                        {activeConclusionDetails
                          ? `${t("evaluation.hide")} ▾`
                          : `${t(
                              "evaluation.add_general_evaluation_comments"
                            )} ▸`}
                      </Link>
                    )}

                    {activeConclusionDetails && (
                      <TextField
                        label={`${t(
                          "evaluation.general_evaluation_comments"
                        )}: `}
                        placeholder={t(
                          "evaluation.add_final_comments_on_development_results_evaluation"
                        )}
                        value={details ?? ""}
                        onChange={(e) => setDetails(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={5}
                        sx={{
                          mt: details.trim() === "" ? 0 : 1.5,
                          "& .MuiInputBase-input": {
                            fontSize: "0.9rem",
                            my: -1,
                          },
                        }}
                        inputRef={aditionalDetailsRef}
                        disabled={isLoading}
                      />
                    )}
                  </Box>
                </Box>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="flex-end"
                  spacing={1}
                  mt={2}
                  mb={1}
                >
                  {/* Cancel (visible while editing)
                  ONLY show if there is backend data (interview.evaluation) */}
                  {evaluation ? (
                    <EGButton
                      label={t("evaluation.cancel")}
                      onClick={handleCancelEdit}
                      loading={isLoading}
                      color="secondary"
                    />
                  ) : null}

                  {/* Save */}
                  <EGButton
                    color="success"
                    labelColor="white"
                    label={t("evaluation.save")}
                    onClick={handleSubmit}
                    loading={isLoading}
                  />
                </Stack>
              </Box>
            </>
          ) : (
            <>
              {/* View mode: show saved evaluation */}
              <Box sx={{ mb: 1 }}>
                {evaluationPoints.map(({ key, value }, index) => (
                  <Box
                    key={index}
                    sx={{
                      borderBottom:
                        index < evaluationPoints.length - 1 &&
                        `1px solid ${theme.palette.blueGray50.main}`,
                      py: 0.5,
                    }}
                  >
                    <Typography
                      variant="body1"
                      component="li"
                      fontWeight={600}
                      ml={2}
                    >
                      {`${pointsToEvaluateLabels[key] || key}`}
                    </Typography>
                    <Typography
                      sx={{ color: "text.secondary", py: 0.33, px: 2 }}
                      fontSize={13}
                      fontWeight={600}
                    >
                      – {t(`evaluation.${[value.value]}`)}
                    </Typography>
                    {value?.details && value?.details?.trim() !== "" && (
                      <Typography
                        variant="body2"
                        lineHeight={1.4}
                        textAlign="justify"
                        fontSize={13}
                        sx={{
                          my: 0.5,
                          ml: 1.5,
                          mb: 1,
                          borderLeft: "4px solid",
                          borderColor: "grey.200",
                          display: "block",
                          p: 0.5,
                          borderRadius: 1,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        <Typography
                          fontWeight={600}
                          component="span"
                          fontSize={13}
                        >
                          {t("evaluation.specific_comment")}:
                        </Typography>{" "}
                        {value.details}
                      </Typography>
                    )}
                  </Box>
                ))}
                {evaluation?.details && evaluation?.details?.trim() !== "" && (
                  <Box
                    sx={{
                      borderTop: `2px solid ${theme.palette.blueGray200.main}`,
                      pt: 0.5,
                      pb: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight={600} mt={1}>
                      {t("evaluation.general_evaluation_comments")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      lineHeight={1.4}
                      textAlign="justify"
                      fontSize={13}
                      p={0.5}
                      sx={{
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {evaluation.details}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Paper>
      </>
    );
  }
);

Analysis.displayName = "Analysis";
